import React, { useEffect, useState } from "react";
import { X, Download, Smartphone } from "lucide-react";

/**
 * InstallPWA — floating "Install app" prompt.
 * - On Chrome/Edge/Android: shows a button that fires the deferred `beforeinstallprompt` event.
 * - On iOS Safari (no install event): shows a step-by-step "Share → Add to Home Screen" hint.
 * - Dismiss persists for 30 days.
 */
export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const dismissedAt = parseInt(localStorage.getItem("easystay_pwa_dismissed") || "0");
    const dismissedRecently = Date.now() - dismissedAt < 30 * 24 * 60 * 60 * 1000;

    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    const ua = window.navigator.userAgent;
    const iosDevice = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    setIsIOS(iosDevice);

    if (dismissedRecently || standalone) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS never fires the install event — show the manual hint after a short delay
    if (iosDevice) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBip); };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem("easystay_pwa_dismissed", String(Date.now()));
    setVisible(false);
  };

  if (!visible || isStandalone) return null;

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md sm:left-auto sm:right-6 sm:bottom-24">
      <div className="rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-copper text-white">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-sm font-bold text-slate-900">Install EasyStay</div>
            {isIOS ? (
              <p className="mt-1 text-xs text-slate-600">
                Tap <strong>Share</strong> → <strong>Add to Home Screen</strong> to install EasyStay like a real app.
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-600">Get one-tap access to bookings, keys and payments — right from your home screen.</p>
            )}
            {!isIOS && deferredPrompt && (
              <button
                data-testid="pwa-install-btn"
                onClick={install}
                className="btn-primary mt-3 !py-2 !px-4 text-xs"
              >
                <Download className="mr-1 inline h-3.5 w-3.5" /> Install app
              </button>
            )}
          </div>
          <button onClick={dismiss} data-testid="pwa-dismiss-btn" className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
